import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { CreditCard, HelpCircle, CheckCircle2, Split as SplitIcon } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import LoadingScreen from '../components/ui/LoadingScreen';
import UserAvatar from '../components/ui/UserAvatar';
import QBDealPicker, { QBDealOption } from '../components/sales-activity/QBDealPicker';
import TransactionSplitEditor from '../components/sales-activity/TransactionSplitEditor';
import CardExpenseHelpModal from '../components/card-expenses/CardExpenseHelpModal';
import { useCardExpenseTour } from '../components/card-expenses/CardExpenseTour';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canManageCardExpensesOnBehalf } from '../utils/cardExpenseAccess';
import {
  useCardExpenseAccess,
  usePendingCardExpenses,
  useRecentlyCategorizedCardExpenses,
  useUserCardAssignments,
} from '../hooks/useCardExpenses';
import {
  categorizeCardExpense,
  categorizeCardExpensesBulk,
  syncCardExpenseToQuickBooks,
  CARD_EXPENSE_OVERHEAD,
  CARD_EXPENSE_EXCLUDE,
  QB_EXPENSE_EXCLUDE_ASSIGN,
} from '../utils/cardExpensesApi';
import { QB_WRITE_BACK_PAUSED, QB_WRITE_BACK_PAUSED_MESSAGE } from '../utils/qbWriteBackConfig';
import { stripSequenceCardSuffix } from '../utils/cardExpenseMatch';
import { expenseDedupeKey } from '../utils/cardExpenseDedup';
import { rankDealsForQbTransaction } from '../utils/qbDealSuggest';
import { QB_EXPENSE_OVERHEAD_ASSIGN } from '../utils/qbTransactionLinks';
import { planTransactionSplitSave, round2, seedSplitDraft } from '../utils/transactionSplits';
import { buildQBLinkDealOptions, encodeQBLinkTargetId, parseQBLinkTargetId } from '../utils/qbDealLinkOptions';
import {
  fetchExpenseLegend,
  qbAccountShortLabel,
  type ExpenseLegendRow,
} from '../utils/expenseLegend';
import { inferExpenseCategory, type InferExpenseCategoryResult } from '../utils/inferExpenseCategory';
import {
  categorizeCardExpenseFromPurchaseOrder,
  categorizeCardExpenseToPurchaseOrder,
  toCardPoMatchRequisition,
} from '../utils/cardExpensePurchaseApply';
import {
  linkedQbExpenseIds,
  parseCardPoPickerId,
  purchaseOrderPickerOptions,
  rankPurchaseOrdersForExpense,
} from '../utils/cardExpensePurchaseMatch';
import { subscribePurchaseRequisitions } from '../utils/purchasingRequisitions';
import type { PurchaseRequisition } from '../types/purchasing';
import type { EnhancedDeal, QBExpense, TransactionSplit, ProjectPLBucket } from '../types';
import './CardExpensesPage.css';

const TABLE_COL_COUNT = 8;

interface CardholderMeta {
  name: string;
  userId?: string;
  photoURL?: string | null;
}

function formatCurrency(n: number): string {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCurrencyFull(n: number): string {
  return formatCurrency(n);
}

function formatDate(txnDate: string): string {
  if (!txnDate) return '—';
  const d = new Date(`${txnDate}T12:00:00`);
  return Number.isNaN(d.getTime()) ? txnDate : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const SPECIAL_DEAL_OPTIONS: QBDealOption[] = [
  { id: QB_EXPENSE_OVERHEAD_ASSIGN, label: 'Overhead' },
  { id: '', label: 'Leave unmatched' },
  { id: QB_EXPENSE_EXCLUDE_ASSIGN, label: 'Exclude from P&L' },
];

/** True when the picker has a saveable destination (project/PO, overhead, or exclude). */
function cardExpensePickerReady(value: string | null): boolean {
  if (value === null || value === '') return false;
  if (value === QB_EXPENSE_EXCLUDE_ASSIGN || value === QB_EXPENSE_OVERHEAD_ASSIGN) return true;
  if (parseCardPoPickerId(value)) return true;
  return Boolean(parseQBLinkTargetId(value).dealId);
}

function resolveCategorizeTarget(selected: string): { target: string | null; linkTargetId: string | null } {
  if (selected === QB_EXPENSE_OVERHEAD_ASSIGN) {
    return { target: CARD_EXPENSE_OVERHEAD, linkTargetId: selected };
  }
  if (selected === QB_EXPENSE_EXCLUDE_ASSIGN) {
    return { target: CARD_EXPENSE_EXCLUDE, linkTargetId: selected };
  }
  const parsed = parseQBLinkTargetId(selected || null);
  return { target: parsed.dealId, linkTargetId: selected || null };
}

function targetDealTitle(
  target: string | null,
  linkTargetId: string | null,
  dealOptions: QBDealOption[],
): string | undefined {
  if (!target || target === CARD_EXPENSE_OVERHEAD || target === CARD_EXPENSE_EXCLUDE) return undefined;
  const lookup = linkTargetId || target;
  return dealOptions.find(o => o.id === lookup || o.id === target)?.label;
}

function qbSyncChip(expense: QBExpense & { id: string }): {
  label: string;
  tone: 'ok' | 'wait' | 'err' | 'muted';
  title?: string;
} {
  if (QB_WRITE_BACK_PAUSED) {
    return { label: 'QB sync paused', tone: 'muted', title: QB_WRITE_BACK_PAUSED_MESSAGE };
  }
  if (expense.excluded) return { label: 'Excluded', tone: 'muted' };
  switch (expense.qbWriteBackStatus) {
    case 'synced':
      return { label: 'In QuickBooks ✓', tone: 'ok' };
    case 'pending':
      return { label: 'Syncing to QuickBooks…', tone: 'wait' };
    case 'paused':
      return { label: 'QB sync paused', tone: 'muted', title: QB_WRITE_BACK_PAUSED_MESSAGE };
    case 'failed':
      return { label: 'QuickBooks sync failed', tone: 'err', title: expense.qbWriteBackError };
    case 'skipped':
      if (String(expense.quickbooksId || '').startsWith('sequence:')) {
        return {
          label: 'Waiting for QuickBooks match',
          tone: 'wait',
          title: expense.qbWriteBackError,
        };
      }
      return { label: 'Not synced', tone: 'muted', title: expense.qbWriteBackError };
    default:
      return { label: '—', tone: 'muted' };
  }
}

function categorizedDestination(
  expense: QBExpense & { id: string },
  legendRows: ExpenseLegendRow[],
): string {
  if (expense.excluded) return 'Excluded from P&L';
  if (expense.splits && expense.splits.length > 1) {
    return `Split across ${expense.splits.length} projects`;
  }
  const accountPick = expense.qbAccountChoiceName
    ? ` · ${qbAccountShortLabel({
        qbAccountId: expense.qbAccountChoiceId || '',
        qbAccountName: expense.qbAccountChoiceName,
      })}`
    : '';
  const poPrefix = expense.poNumber ? `${expense.poNumber} · ` : '';
  if (expense.category === 'overhead') {
    const legend = expense.plBucketLegendId
      ? legendRows.find(r => r.id === expense.plBucketLegendId)
      : null;
    return `${poPrefix}${legend ? `Overhead — ${legend.label}${accountPick}` : 'Company overhead'}`;
  }
  return `${poPrefix}${(expense.matchedDealTitle || 'Unmatched') + accountPick}`;
}

interface InboxRowProps {
  expense: QBExpense & { id: string };
  dealOptions: QBDealOption[];
  dealTitleById: Record<string, string>;
  wonDeals: EnhancedDeal[];
  cardholder: CardholderMeta;
  showCardholder: boolean;
  selected: boolean;
  /** Expense legend groups (Travel, Install, …). Defaults to empty if omitted. */
  legendRows?: ExpenseLegendRow[];
  purchaseOrders: PurchaseRequisition[];
  onToggleSelect: (id: string) => void;
  onDone: (expenseId: string) => void;
}

/** Project link + P&L category (Travel, Install, …). No per-account “Which kind” pick — QB write-back uses the group default. */
function cardExpenseCategorizationMeta(
  isOverhead: boolean,
  isExclude: boolean,
  category: InferExpenseCategoryResult,
  categoryTouched: boolean,
) {
  if (isExclude) {
    return {
      legendId: null as string | null,
      plBucket: undefined as ProjectPLBucket | undefined,
      plBucketSource: 'auto' as const,
      qbAccountId: null as string | null,
      qbAccountName: null as string | null,
    };
  }
  if (isOverhead) {
    return {
      legendId: null as string | null,
      plBucket: 'overhead' as ProjectPLBucket,
      plBucketSource: 'user' as const,
      qbAccountId: null as string | null,
      qbAccountName: null as string | null,
    };
  }
  return {
    legendId: category.legendId,
    plBucket: category.plBucket,
    plBucketSource: categoryTouched ? ('user' as const) : ('auto' as const),
    qbAccountId: null as string | null,
    qbAccountName: null as string | null,
  };
}

const CardExpenseInboxRow: React.FC<InboxRowProps> = ({
  expense,
  dealOptions,
  dealTitleById,
  wonDeals,
  cardholder,
  showCardholder,
  selected,
  legendRows = [],
  purchaseOrders,
  onToggleSelect,
  onDone,
}) => {
  const [pickerValue, setPickerValue] = useState<string | null>(null);
  const [categoryChoice, setCategoryChoice] = useState<InferExpenseCategoryResult | null>(null);
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitDraft, setSplitDraft] = useState<TransactionSplit[]>([]);
  const [splitErr, setSplitErr] = useState<string | null>(null);

  const txnTotal = round2(Number(expense.totalAmount) || 0);

  const displayDescription = useMemo(
    () => stripSequenceCardSuffix(expense.description),
    [expense.description],
  );

  const suggestedIds = useMemo(() => {
    const poIds = rankPurchaseOrdersForExpense(
      {
        id: expense.id,
        vendorName: expense.vendorName,
        description: expense.description,
        totalAmount: expense.totalAmount,
        txnDate: expense.txnDate,
        assignedUserId: expense.assignedUserId,
        categorizationStatus: expense.categorizationStatus,
        purchaseRequisitionId: expense.purchaseRequisitionId,
      },
      purchaseOrders.map(toCardPoMatchRequisition),
    );
    const dealIds = rankDealsForQbTransaction(wonDeals, {
      vendorName: expense.vendorName,
      description: expense.description,
      customerName: expense.customerName,
      lineItems: expense.lineItems,
    })
      .map(s => s.dealId)
      .filter(Boolean);
    return [...poIds, ...dealIds];
  }, [expense, purchaseOrders, wonDeals]);

  const selectedPoId = parseCardPoPickerId(pickerValue);
  const selectedPo = selectedPoId ? purchaseOrders.find(row => row.id === selectedPoId) : undefined;
  const isPoPick = Boolean(selectedPo);
  const selectedPoLinkedCount = selectedPo ? linkedQbExpenseIds(selectedPo).length : 0;

  const autoCategory = useMemo(
    () =>
      inferExpenseCategory(legendRows, {
        vendorName: expense.vendorName,
        description: expense.description,
        accountName: expense.accountName,
      }),
    [expense, legendRows],
  );
  const activeCategory = categoryTouched && categoryChoice ? categoryChoice : autoCategory;

  const projectCategoryRows = useMemo(() => {
    const nonOverhead = legendRows.filter(r => r.plBucket !== 'overhead');
    if (activeCategory.legendId && !nonOverhead.some(r => r.id === activeCategory.legendId)) {
      const extra = legendRows.find(r => r.id === activeCategory.legendId);
      if (extra) return [...nonOverhead, extra];
    }
    return nonOverhead;
  }, [legendRows, activeCategory.legendId]);

  const showProjectCategory =
    cardExpensePickerReady(pickerValue) &&
    !isPoPick &&
    pickerValue !== QB_EXPENSE_OVERHEAD_ASSIGN &&
    pickerValue !== QB_EXPENSE_EXCLUDE_ASSIGN;

  useEffect(() => {
    setCategoryTouched(false);
    setCategoryChoice(null);
  }, [expense.id, autoCategory.legendId]);

  const splitAllocated = useMemo(
    () => splitDraft.reduce((sum, slice) => sum + (Number(slice.amount) || 0), 0),
    [splitDraft],
  );

  const openSplit = () => {
    setSplitErr(null);
    setSplitOpen(true);
    setSplitDraft(seedSplitDraft());
  };

  const cancelSplit = () => {
    setSplitOpen(false);
    setSplitDraft([]);
    setSplitErr(null);
  };

  const updateSplitSlice = (idx: number, patch: Partial<TransactionSplit>) => {
    setSplitDraft(prev => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const submit = async () => {
    if (pickerValue === null) return;
    setSubmitting(true);
    setError(null);
    try {
      if (selectedPo) {
        await categorizeCardExpenseToPurchaseOrder({ expense, requisition: selectedPo });
        onDone(expense.id);
        return;
      }
      const { target, linkTargetId } = resolveCategorizeTarget(pickerValue);
      const parsed = parseQBLinkTargetId(linkTargetId || null);
      const isOverhead = pickerValue === QB_EXPENSE_OVERHEAD_ASSIGN;
      const isExclude = pickerValue === QB_EXPENSE_EXCLUDE_ASSIGN;
      const meta = cardExpenseCategorizationMeta(isOverhead, isExclude, activeCategory, categoryTouched);
      await categorizeCardExpense({
        expenseId: expense.id,
        target,
        linkTargetId,
        projectPhase: parsed.projectPhase,
        dealTitle: targetDealTitle(target, linkTargetId, dealOptions),
        ...meta,
      });
      onDone(expense.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSubmitting(false);
    }
  };

  const saveSplit = async () => {
    const plan = planTransactionSplitSave(txnTotal, splitDraft, dealTitleById);
    if (plan.kind === 'error') {
      setSplitErr(plan.message);
      return;
    }

    setSubmitting(true);
    setSplitErr(null);
    try {
      if (plan.kind === 'single') {
        const linkTargetId =
          plan.projectPhase === 'preconstruction' && plan.dealId
            ? encodeQBLinkTargetId(plan.dealId, 'preconstruction')
            : plan.dealId;
        await categorizeCardExpense({
          expenseId: expense.id,
          target: plan.dealId,
          linkTargetId,
          projectPhase: plan.projectPhase,
          dealTitle: plan.dealTitle,
          ...cardExpenseCategorizationMeta(false, false, activeCategory, categoryTouched),
        });
      } else {
        await categorizeCardExpense({
          expenseId: expense.id,
          splits: plan.splits,
          ...cardExpenseCategorizationMeta(false, false, activeCategory, categoryTouched),
        });
      }
      onDone(expense.id);
    } catch (err) {
      setSplitErr(err instanceof Error ? err.message : 'Could not save split.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <tr data-tour="card-expense-row" className={selected ? 'ce-row-selected' : undefined}>
        <td className="ce-check-col">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(expense.id)}
            aria-label={`Select ${expense.vendorName || 'charge'}`}
          />
        </td>
        {showCardholder && (
          <td className="ce-user-col">
            <UserAvatar
              name={cardholder.name}
              userId={cardholder.userId}
              photoURL={cardholder.photoURL}
              className="ce-user-avatar"
              title={cardholder.name}
            />
          </td>
        )}
        <td>{formatDate(expense.txnDate)}</td>
        <td>
          <strong>{expense.vendorName || 'Vendor'}</strong>
          {expense.adminOnlyExpense && <div className="ce-row-sub">Short Term Ops · external account</div>}
          {displayDescription && <div className="ce-row-sub">{displayDescription}</div>}
        </td>
        <td className="ce-amount">{formatCurrency(txnTotal)}</td>
        <td>
          <code>{expense.cardLastFour || '—'}</code>
        </td>
        <td data-tour="card-expense-picker">
          <div className="ce-row-actions">
            <QBDealPicker
              value={pickerValue ?? ''}
              onChange={v => setPickerValue(v)}
              dealOptions={dealOptions}
              specialOptions={SPECIAL_DEAL_OPTIONS}
              suggestedIds={suggestedIds}
              emptyLabel="Choose project or PO…"
              disabled={splitOpen}
              aria-label="Categorize to project or purchase order"
            />
            {selectedPo && (
              <div className="ce-po-hint">
                Maps via {selectedPo.poNumber || selectedPo.prNumber}
                {selectedPo.costObject?.label ? ` · ${selectedPo.costObject.label}` : ''}
                {selectedPoLinkedCount > 0
                  ? ` · ${selectedPoLinkedCount} charge${selectedPoLinkedCount === 1 ? '' : 's'} already linked`
                  : ''}
              </div>
            )}
            {showProjectCategory && (
              <select
                className={`ce-category-select${activeCategory.confidence === 'none' ? ' ce-category-low' : ''}`}
                value={activeCategory.legendId || ''}
                disabled={splitOpen || submitting}
                aria-label="Project cost category"
                onChange={e => {
                  const row = legendRows.find(r => r.id === e.target.value);
                  if (!row) return;
                  setCategoryTouched(true);
                  setCategoryChoice({
                    legendId: row.id,
                    plBucket: row.plBucket,
                    label: row.label,
                    confidence: 'high',
                    reason: 'user',
                  });
                }}
              >
                {projectCategoryRows.map(row => (
                  <option key={row.id} value={row.id}>
                    {row.label}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="ce-split-btn"
              onClick={() => (splitOpen ? cancelSplit() : openSplit())}
              disabled={submitting}
              title="Split across multiple projects"
            >
              <SplitIcon size={12} aria-hidden />
              Split
            </button>
          </div>
        </td>
        <td>
          <button
            type="button"
            className="ce-submit-btn"
            onClick={submit}
            disabled={submitting || splitOpen || !cardExpensePickerReady(pickerValue)}
          >
            {submitting ? 'Saving…' : 'Save'}
          </button>
          {error && <div className="ce-row-error">{error}</div>}
        </td>
      </tr>
      {splitOpen && (
        <tr className="qbtx-split-editor-row">
          <td colSpan={showCardholder ? TABLE_COL_COUNT : TABLE_COL_COUNT - 1}>
            <TransactionSplitEditor
              txnTotal={txnTotal}
              slices={splitDraft}
              allocated={splitAllocated}
              error={splitErr}
              dealOptions={dealOptions}
              formatCurrencyFull={formatCurrencyFull}
              onUpdateSlice={updateSplitSlice}
              onRemoveSlice={idx => setSplitDraft(prev => prev.filter((_, i) => i !== idx))}
              onAddSlice={() => setSplitDraft(prev => [...prev, { dealId: null, amount: 0 }])}
              onSave={saveSplit}
              onCancel={cancelSplit}
              saving={submitting}
              saveLabel="Save & categorize"
            />
          </td>
        </tr>
      )}
    </>
  );
};

const CardExpensesPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { enhancedDeals, crmUsers, timberProjects } = useCRM();
  const me = useMemo(() => crmUsers.find(u => u.uid === user?.id), [crmUsers, user?.id]);
  const canManageOnBehalf = canManageCardExpensesOnBehalf({
    authRole: user?.role,
    designations: me?.designations ?? user?.designations,
  });
  const { userCards } = useUserCardAssignments(user?.id);
  const { canAccess, hasCardAssignment, loading: accessLoading } = useCardExpenseAccess(
    user?.id,
    user?.role,
    me?.designations ?? user?.designations,
  );
  const { expenses, loading: expensesLoading } = usePendingCardExpenses(user?.id, {
    viewAll: canManageOnBehalf,
    userCards,
  });
  const { expenses: recentCategorized } = useRecentlyCategorizedCardExpenses(user?.id, {
    viewAll: canManageOnBehalf,
    max: canManageOnBehalf ? 15 : 8,
  });
  const [showHelp, setShowHelp] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkTarget, setBulkTarget] = useState<string | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ processed: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [syncingIds, setSyncingIds] = useState<Set<string>>(() => new Set());
  const [syncNotes, setSyncNotes] = useState<Record<string, string>>({});
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [legendRows, setLegendRows] = useState<ExpenseLegendRow[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseRequisition[]>([]);
  const matchingPoIds = useRef<Set<string>>(new Set());
  /** Hide rows immediately after a successful save until Firestore pending queries catch up. */
  const [savedAwayIds, setSavedAwayIds] = useState<Set<string>>(() => new Set());
  const [savedAwayKeys, setSavedAwayKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    fetchExpenseLegend().then(setLegendRows).catch(() => setLegendRows([]));
  }, [refreshKey]);

  useEffect(() => {
    if (!user?.id) {
      setPurchaseOrders([]);
      return;
    }
    return subscribePurchaseRequisitions({ userId: user.id, viewAll: canManageOnBehalf }, rows => {
      setPurchaseOrders(
        rows.filter(
          row => row.status === 'approved' || row.status === 'purchased' || Boolean(row.poNumber),
        ),
      );
    });
  }, [user?.id, canManageOnBehalf]);

  useEffect(() => {
    if (savedAwayIds.size === 0 && savedAwayKeys.size === 0) return;
    const pendingIds = new Set(expenses.map(e => e.id));
    const pendingKeys = new Set(
      expenses.map(e => expenseDedupeKey(e)).filter((k): k is string => Boolean(k)),
    );
    setSavedAwayIds(prev => {
      const next = new Set(Array.from(prev).filter(id => pendingIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setSavedAwayKeys(prev => {
      const next = new Set(Array.from(prev).filter(key => pendingKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [expenses, savedAwayIds.size, savedAwayKeys.size]);

  const wonDeals = useMemo(() => enhancedDeals.filter(d => d.outcome === 'won'), [enhancedDeals]);

  const linkDealOptions = useMemo(
    () => buildQBLinkDealOptions(enhancedDeals, timberProjects),
    [enhancedDeals, timberProjects],
  );

  const projectDealOptions: QBDealOption[] = useMemo(
    () => linkDealOptions.map(o => ({ id: o.id, label: o.label, searchText: o.searchText })),
    [linkDealOptions],
  );

  const dealTitleById = useMemo(
    () => Object.fromEntries(linkDealOptions.map(o => [o.id, o.label])),
    [linkDealOptions],
  );

  const poOptions = useMemo(
    () => purchaseOrderPickerOptions(purchaseOrders.map(toCardPoMatchRequisition)),
    [purchaseOrders],
  );

  const dealOptions: QBDealOption[] = useMemo(
    () => [...projectDealOptions, ...poOptions],
    [projectDealOptions, poOptions],
  );

  const userMetaById = useMemo(
    () =>
      Object.fromEntries(
        crmUsers.map(u => [u.uid, { name: u.displayName, photoURL: u.photoURL }]),
      ),
    [crmUsers],
  );

  const visibleExpenses = useMemo(
    () =>
      expenses.filter(e => {
        if (savedAwayIds.has(e.id)) return false;
        const key = expenseDedupeKey(e);
        if (key && savedAwayKeys.has(key)) return false;
        return true;
      }),
    [expenses, savedAwayIds, savedAwayKeys],
  );

  const showCardholder =
    canManageOnBehalf || new Set(visibleExpenses.map(e => e.assignedUserId).filter(Boolean)).size > 1;

  const expenseIds = useMemo(() => visibleExpenses.map(e => e.id), [visibleExpenses]);
  const allSelected = expenseIds.length > 0 && expenseIds.every(id => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const { startTour } = useCardExpenseTour(visibleExpenses.length > 0);
  const loading = authLoading || accessLoading || expensesLoading;

  const markExpensesSavedAway = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setSavedAwayIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
    setSavedAwayKeys(prev => {
      const next = new Set(prev);
      for (const id of ids) {
        const row = expenses.find(e => e.id === id);
        const key = row ? expenseDedupeKey(row) : null;
        // Only Sequence twins share a stable charge identity across docs.
        if (key && key.startsWith('seq:')) next.add(key);
      }
      return next;
    });
  }, [expenses]);

  useEffect(() => {
    if (expenses.length === 0 || purchaseOrders.length === 0) return;
    expenses.forEach(expense => {
      if (matchingPoIds.current.has(expense.id)) return;
      matchingPoIds.current.add(expense.id);
      categorizeCardExpenseFromPurchaseOrder({ expense, requisitions: purchaseOrders })
        .then(poNumber => {
          if (poNumber) markExpensesSavedAway([expense.id]);
          else matchingPoIds.current.delete(expense.id);
        })
        .catch(() => {
          matchingPoIds.current.delete(expense.id);
        });
    });
  }, [expenses, purchaseOrders, markExpensesSavedAway]);

  const handleDone = useCallback((expenseId?: string) => {
    if (expenseId) markExpensesSavedAway([expenseId]);
    setRefreshKey(k => k + 1);
    setSelectedIds(new Set());
    setBulkTarget(null);
    setBulkMessage(null);
  }, [markExpensesSavedAway]);

  const handleManualQbSync = useCallback(async (expenseId: string) => {
    setSyncingIds(prev => new Set(prev).add(expenseId));
    setSyncNotes(prev => {
      const next = { ...prev };
      delete next[expenseId];
      return next;
    });
    try {
      const result = await syncCardExpenseToQuickBooks({ expenseId });
      const status = result.qbWriteBackStatus || 'done';
      const detail = result.qbWriteBackError ? ` — ${result.qbWriteBackError}` : '';
      const linkNote =
        result.linkedExpenseId !== expenseId ? ` (linked ${result.linkedExpenseId})` : '';
      setSyncNotes(prev => ({
        ...prev,
        [expenseId]: `${status}${linkNote}${detail}`,
      }));
    } catch (err) {
      setSyncNotes(prev => ({
        ...prev,
        [expenseId]: err instanceof Error ? err.message : 'Sync failed',
      }));
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev);
        next.delete(expenseId);
        return next;
      });
    }
  }, []);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setBulkMessage(null);
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (expenseIds.length > 0 && expenseIds.every(id => prev.has(id))) {
        return new Set();
      }
      return new Set(expenseIds);
    });
    setBulkMessage(null);
  }, [expenseIds]);

  const applyBulk = async () => {
    if (bulkTarget === null || selectedIds.size === 0) return;
    const total = selectedIds.size;
    setBulkSubmitting(true);
    setBulkProgress({ processed: 0, total });
    setBulkError(null);
    setBulkMessage(null);
    try {
      const bulkPoId = parseCardPoPickerId(bulkTarget);
      if (bulkPoId) {
        const requisition = purchaseOrders.find(row => row.id === bulkPoId);
        if (!requisition) throw new Error('Purchase order not found.');
        const ids = Array.from(selectedIds);
        let updated = 0;
        for (let i = 0; i < ids.length; i += 1) {
          const expense = expenses.find(row => row.id === ids[i]);
          setBulkProgress({ processed: i, total });
          if (!expense) continue;
          await categorizeCardExpenseToPurchaseOrder({ expense, requisition });
          updated += 1;
        }
        setBulkProgress({ processed: ids.length, total });
        markExpensesSavedAway(ids);
        setRefreshKey(k => k + 1);
        setSelectedIds(new Set());
        setBulkTarget(null);
        setBulkMessage(
          updated > 0
            ? `${updated} mapped to ${requisition.poNumber || requisition.prNumber}`
            : 'No changes applied.',
        );
        return;
      }
      const { target, linkTargetId } = resolveCategorizeTarget(bulkTarget);
      const parsed = parseQBLinkTargetId(linkTargetId || null);
      const isBulkOverhead = bulkTarget === QB_EXPENSE_OVERHEAD_ASSIGN;
      const isBulkExclude = bulkTarget === QB_EXPENSE_EXCLUDE_ASSIGN;
      const bulkDefault =
        isBulkOverhead || isBulkExclude ? null : inferExpenseCategory(legendRows, {});
      const result = await categorizeCardExpensesBulk({
        expenseIds: Array.from(selectedIds),
        target,
        linkTargetId,
        projectPhase: parsed.projectPhase,
        dealTitle: targetDealTitle(target, linkTargetId, dealOptions),
        legendId: isBulkOverhead || isBulkExclude ? null : bulkDefault?.legendId,
        plBucket: isBulkOverhead ? 'overhead' : bulkDefault?.plBucket,
        qbAccountId: null,
        qbAccountName: null,
        onProgress: (processed, batchTotal) => setBulkProgress({ processed, total: batchTotal }),
      });
      const parts: string[] = [];
      if (result.updated > 0) parts.push(`${result.updated} updated`);
      if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
      setBulkMessage(parts.length > 0 ? parts.join(', ') : 'No changes applied.');
      markExpensesSavedAway(Array.from(selectedIds));
      handleDone();
    } catch (err) {
      setBulkError(err instanceof Error ? err.message : 'Bulk update failed.');
    } finally {
      setBulkSubmitting(false);
      setBulkProgress(null);
    }
  };

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccess) return <Navigate to="/" replace />;

  return (
    <div className="card-expenses-page">
      <AppHeader />
      <main className="card-expenses-main">
        <header className="card-expenses-header">
          <div>
            <h1 data-tour="card-expense-title">
              <CreditCard size={22} aria-hidden />
              My Card Expenses
            </h1>
            <p>
              Categorize company card charges to a project, a purchase order, split across multiple
              projects, assign to company overhead, or leave unmatched. Several charges can share one PO.
              {canManageOnBehalf && ' You can categorize pending card charges for the whole team.'}
            </p>
          </div>
          <div className="card-expenses-header-actions">
            <button
              type="button"
              className="ce-help-btn"
              data-tour="card-expense-help"
              onClick={() => {
                setShowHelp(true);
                startTour();
              }}
            >
              <HelpCircle size={16} aria-hidden />
              Help
            </button>
          </div>
        </header>

        {visibleExpenses.length === 0 ? (
          <section className="card-expenses-empty" data-tour="card-expense-empty">
            <CheckCircle2 size={32} aria-hidden />
            <h2>{hasCardAssignment ? 'All caught up' : 'No card assigned yet'}</h2>
            <p>
              {hasCardAssignment
                ? 'No pending card charges assigned to you.'
                : 'Ask an admin to assign your company card in Debit Card Settings (Project P&L). After charges sync from QuickBooks, they will appear here for categorization.'}
            </p>
          </section>
        ) : (
          <section className="card-expenses-inbox" data-tour="card-expense-inbox">
            <p className="card-expenses-count">
              <strong>{visibleExpenses.length}</strong> pending charge{visibleExpenses.length === 1 ? '' : 's'}
            </p>

            {someSelected && (
              <div className="ce-bulk-bar" data-tour="card-expense-bulk">
                <span className="ce-bulk-count">
                  <strong>{selectedIds.size}</strong> selected
                </span>
                <QBDealPicker
                  value={bulkTarget ?? ''}
                  onChange={v => {
                    setBulkTarget(v);
                    setBulkError(null);
                  }}
                  dealOptions={dealOptions}
                  specialOptions={SPECIAL_DEAL_OPTIONS}
                  emptyLabel="Apply project or PO to selected…"
                  aria-label="Bulk categorize to project or purchase order"
                />
                <button
                  type="button"
                  className="ce-submit-btn ce-bulk-apply"
                  onClick={applyBulk}
                  disabled={bulkSubmitting || bulkTarget === null}
                >
                  {bulkSubmitting && bulkProgress
                    ? `Applying… ${bulkProgress.processed}/${bulkProgress.total}`
                    : bulkSubmitting
                      ? 'Applying…'
                      : `Apply to ${selectedIds.size}`}
                </button>
                <button
                  type="button"
                  className="ce-bulk-clear"
                  onClick={() => {
                    setSelectedIds(new Set());
                    setBulkTarget(null);
                    setBulkError(null);
                    setBulkMessage(null);
                  }}
                >
                  Clear
                </button>
                {bulkError && <div className="ce-bulk-error">{bulkError}</div>}
                {bulkMessage && !bulkError && <div className="ce-bulk-message">{bulkMessage}</div>}
              </div>
            )}

            <div className="card-expenses-table-wrap">
              <table className="card-expenses-table">
                <thead>
                  <tr>
                    <th className="ce-check-col">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-label={allSelected ? 'Deselect all' : 'Select all'}
                      />
                    </th>
                    {showCardholder && <th className="ce-user-col" aria-label="Cardholder" />}
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Amount</th>
                    <th>Card</th>
                    <th>Project / PO</th>
                    <th />
                  </tr>
                </thead>
                <tbody key={refreshKey}>
                  {visibleExpenses.map(exp => {
                    const uid = exp.assignedUserId || '';
                    const meta = uid ? userMetaById[uid] : undefined;
                    return (
                      <CardExpenseInboxRow
                        key={exp.id}
                        expense={exp}
                        dealOptions={dealOptions}
                        dealTitleById={dealTitleById}
                        wonDeals={wonDeals}
                        legendRows={legendRows}
                        purchaseOrders={purchaseOrders}
                        showCardholder={showCardholder}
                        cardholder={{
                          name: exp.assignedUserName || meta?.name || 'Cardholder',
                          userId: uid || undefined,
                          photoURL: meta?.photoURL,
                        }}
                        selected={selectedIds.has(exp.id)}
                        onToggleSelect={toggleSelect}
                        onDone={handleDone}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {recentCategorized.length > 0 && (
          <section className="ce-recent-section">
            <h2>Recently categorized</h2>
            <p className="ce-recent-sub">
              {QB_WRITE_BACK_PAUSED
                ? 'QuickBooks sync is paused — saves here update the CRM only and do not change QuickBooks.'
                : 'Each save updates the CRM and pushes the expense account to QuickBooks automatically.'}
            </p>
            <div className="card-expenses-table-wrap">
              <table className="card-expenses-table ce-recent-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Vendor</th>
                    <th>Amount</th>
                    <th>Filed to</th>
                    <th>QuickBooks</th>
                  </tr>
                </thead>
                <tbody>
                  {recentCategorized.map(exp => {
                    const chip = qbSyncChip(exp);
                    const syncing = syncingIds.has(exp.id);
                    const syncNote = syncNotes[exp.id];
                    return (
                      <tr key={exp.id}>
                        <td>{formatDate(exp.txnDate)}</td>
                        <td>
                          <strong>{exp.vendorName || 'Vendor'}</strong>
                          {canManageOnBehalf && exp.assignedUserName && (
                            <div className="ce-row-sub">{exp.assignedUserName}</div>
                          )}
                        </td>
                        <td className="ce-amount">{formatCurrency(round2(Number(exp.totalAmount) || 0))}</td>
                        <td>{categorizedDestination(exp, legendRows)}</td>
                        <td>
                          <div className="ce-sync-cell">
                            <span className={`ce-sync-chip ce-sync-${chip.tone}`} title={chip.title}>
                              {chip.label}
                            </span>
                            {!QB_WRITE_BACK_PAUSED && (
                              <button
                                type="button"
                                className="ce-sync-btn"
                                disabled={syncing}
                                onClick={() => handleManualQbSync(exp.id)}
                                title="Link to QuickBooks purchase (if needed) and push categorization"
                              >
                                {syncing ? 'Syncing…' : 'Sync'}
                              </button>
                            )}
                            {syncNote && (
                              <div className="ce-sync-note" title={syncNote}>
                                {syncNote}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      {showHelp && <CardExpenseHelpModal onClose={() => setShowHelp(false)} onStartTour={startTour} />}
    </div>
  );
};

export default CardExpensesPage;
